import React, {useState} from "react";
import styled from "styled-components";
import SubscriptionForm from "./SubscriptionForm";
import HackerNoonLogo from "./HackerNoonLogo";
import {logEvent} from "../../utils/analytics";

// Theme tokens (mirrors homepage dark footer)
const C = {
  dark: "#1e1e1e",
  border: "#374151",
  text: "#e5e7eb",
  textStrong: "#ffffff",
  accent: "#00ff00",
  accentText: "#111111",
};

const SM = "@media (min-width: 640px)";
const LG = "@media (min-width: 1024px)";
const HN_FONT = `"HackerNoon2", "HackerNoon", sans-serif`;

/* ---------- Layout ---------- */

const FooterLayout = styled.footer`
  overflow: hidden;
  background: ${C.dark};
  border-top: 1px solid ${C.border};
  color: ${C.text};
  font-size: 14px;
  padding: 40px 0;
  font-family: "IBM Plex Mono", "ibm-plex-mono", monospace;

  ${LG} {
    padding: 64px 0;
  }
`;

const FooterInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px;

  ${SM} {
    padding: 0 24px;
  }

  ${LG} {
    padding: 0 32px;
  }
`;

/* ---------- Top: GIF | Newsletter + apps ---------- */

const Top = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${C.accent};

  ${LG} {
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    padding-bottom: 40px;
  }
`;

const TopCol = styled.div`
  width: 100%;
  min-width: 0;

  ${LG} {
    align-self: center;
  }

  img.caveman {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const NewsletterTitle = styled.h2`
  font-family: ${HN_FONT};
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${C.accent};
  margin: 0 0 8px;
  line-height: 1.2;

  ${LG} {
    font-size: 24px;
  }
`;

const NewsletterBlurb = styled.p`
  color: ${C.text};
  margin: 0 0 16px;
`;

// Footer-only overrides: hide SubscriptionForm's own heading, stretch the email field
const SubscriptionWrap = styled.div`
  h1,
  h2,
  h3 {
    display: none;
  }

  form {
    width: 100%;
    max-width: none;
  }

  input[type="email"] {
    width: 100%;
    max-width: none;
    margin-left: 0;
    margin-right: 0;
  }
`;

const AppGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  margin-top: 24px;

  ${LG} {
    gap: 16px;
  }
`;

const AppButton = styled.a`
  display: flex;
  align-items: center;
  border: 2px solid ${C.accent};
  background: ${C.dark};
  color: ${C.textStrong};
  padding: 8px 12px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.15s ease;

  ${LG} {
    padding: 12px 16px;
  }

  &:hover {
    background: ${C.accent};
    color: ${C.accentText};
  }

  i {
    line-height: 1;
    flex-shrink: 0;
    font-size: ${p => p.$icon[0]}px;

    ${LG} {
      font-size: ${p => p.$icon[1]}px;
    }
  }

  .labels {
    display: flex;
    flex-direction: column;
    margin-left: 8px;
    line-height: 1.25;

    ${LG} {
      margin-left: 12px;
    }
  }

  .kicker {
    font-size: 10px;

    ${LG} {
      font-size: 12px;
    }
  }

  .store {
    font-size: 16px;
    font-weight: 700;

    ${LG} {
      font-size: 20px;
    }
  }
`;

/* ---------- Links: 5-col grid (desktop) / accordions (mobile) ---------- */

const Nav = styled.nav`
  padding: 24px 0;
  border-bottom: 1px solid ${C.accent};

  ${LG} {
    padding: 40px 0;
  }
`;

const LinksGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${LG} {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 32px;
  }
`;

const AccordionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid ${C.accent};
  border-radius: 4px;
  background: transparent;
  color: ${C.accent};
  font-family: ${HN_FONT};
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: left;
  cursor: pointer;

  ${LG} {
    display: none;
  }

  .plus {
    font-size: 24px;
    line-height: 1;
    transition: transform 0.2s ease;
    transform: ${p => (p.$open ? "rotate(45deg)" : "none")};
  }
`;

const DesktopHeading = styled.h2`
  display: none;
  font-family: ${HN_FONT};
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1.2;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${C.accent};

  ${LG} {
    display: block;
  }
`;

const LinkList = styled.ul`
  display: ${p => (p.$open ? "grid" : "none")};
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  row-gap: 12px;
  list-style: none;
  margin: 0;
  padding: 16px 12px 8px;
  font-size: 14px;

  ${SM} {
    font-size: 16px;
  }

  ${LG} {
    display: block;
    padding: 0;
  }
`;

const LinkItem = styled.li`
  min-width: 0;
  overflow-wrap: break-word;

  ${LG} {
    margin-bottom: 8px;
  }
`;

const MobileOnlyItem = styled.li`
  grid-column: span 2;

  ${LG} {
    display: none;
  }
`;

const FancySpan = styled.span`
  color: ${p => (p.$isHeader ? C.accent : C.text)};
  cursor: pointer;
  transition: all 0.2s ease;

  ${p =>
    !p.$isHeader &&
    `
    &:hover {
      text-decoration: underline;
      color: ${C.textStrong};
      font-weight: 700;
    }
  `}
`;

/* ---------- Socials ---------- */

const Socials = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 16px;
  justify-items: center;
  padding: 24px 0;

  ${LG} {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 16px;
    padding: 32px 0;
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: transform 0.15s ease;

    &:hover {
      transform: scale(1.25);
    }
  }

  i {
    font-size: 24px;
    color: ${C.accent};
    transition: color 0.15s ease;
  }

  a:hover i {
    color: ${C.textStrong};
  }
`;

/* ---------- Brand + copyright ---------- */

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${LG} {
    flex-direction: row;
    gap: 16px;
  }

  a {
    display: flex;
    align-items: center;
  }
`;

const Divider = styled.span`
  display: none;
  height: 24px;
  width: 1px;
  background: ${C.accent};

  ${LG} {
    display: block;
  }
`;

const Tagline = styled.p`
  margin: 0;
  color: ${C.accent};
  font-family: ${HN_FONT};
  font-size: 16px;

  ${LG} {
    font-size: 18px;
  }
`;

const Copyright = styled.p`
  margin: 16px 0 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.625;
  color: ${C.text};

  ${LG} {
    font-size: 14px;
  }
`;

const DesktopOnly = styled.span`
  display: none;

  ${LG} {
    display: inline;
  }
`;

const MobileBreak = styled.br`
  ${LG} {
    display: none;
  }
`;

/* ---------- Data ---------- */

const HN_SOCIALS = {
  x: "https://twitter.com/hackernoon",
  github: "https://github.com/hackernoon",
  youtube: "https://www.youtube.com/c/hackernoon",
  instagram: "https://instagram.com/hackernoon",
  "facebook-square": "https://facebook.com/hackernoon",
  kaggle: "https://www.kaggle.com/hackernoon",
  podcasts: "https://hackernoon.com/p/hackernoon-podcast",
  rss: "https://hackernoon.com/feed",
  "google-news": "https://news.google.com/search?q=hackernoon&hl=en-US&gl=US&ceid=US%3Aen",
  pinterest: "https://www.pinterest.com/hackernoon/",
  giphy: "https://giphy.com/hackernoon",
  mastodon: "https://mas.to/@hackernoon",
  minds: "https://www.minds.com/hackernoon/",
  reddit: "https://www.reddit.com/r/hackernoon/",
  "product-hunt": "https://www.producthunt.com/products/hacker-noon",
  bloomberg: "https://www.bloomberg.com/profile/company/1654159D:US",
  figma: "https://www.figma.com/@hackernoon",
  wikipedia: "https://en.wikipedia.org/wiki/HackerNoon",
  bluesky: "https://bsky.app/profile/hackernoon.com",
  threads: "https://www.threads.com/@hackernoon",
  newsbreak: "https://www.newsbreak.com/m/hackernoon-560145",
  npm: "https://www.npmjs.com/org/hackernoon",
  viewblocks: "https://viewblock.io/arweave/address/X8se6ANj4C-gpP_JH0ZbtJJEpyHBr0XQA-crCpbZGak",
  huggingface: "https://huggingface.co/HackerNoon",
  crunchbase: "https://www.crunchbase.com/organization/hackernoon",
  linkedin: "https://www.linkedin.com/company/hackernoon/",
};

const APP_LINKS = [
  {
    href: "https://apps.apple.com/us/app/hackernoon/id6447998105",
    icon: "hn-apple",
    iconSize: [30, 36],
    kicker: "Download on the",
    label: "App Store",
  },
  {
    href: "https://play.google.com/store/apps/details?id=com.hackernoon.mobile",
    icon: "hn-play-solid",
    iconSize: [24, 30],
    kicker: "Get it on",
    label: "Google Play",
  },
];

const titleCase = (text = "") => text.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());

const logFooterClick = text => {
  logEvent("Footer Links", `${text} was clicked`, `The ${text} Footer link was clicked`);
};

/* ---------- Components ---------- */

const FooterLink = ({href, isHeader, children}) => (
  <a style={{textDecoration: "none"}} href={href} onClick={() => logFooterClick(children)}>
    <FancySpan $isHeader={isHeader}>{children}</FancySpan>
  </a>
);

const Footer = ({footerData = [], staticData = {}, colors}) => {
  // Mobile accordion: index of the open section (one at a time)
  const [openSection, setOpenSection] = useState(null);
  const currentYear = new Date().getFullYear();
  const sections = Array.isArray(footerData) ? footerData : [];

  if (sections.length === 0) {
    return null;
  }

  return (
    <FooterLayout className="footer">
      <FooterInner>
        {/* Top — desktop: GIF | Newsletter (+ apps); mobile: stacked */}
        <Top>
          <TopCol>
            <img
              className="caveman"
              src="https://hackernoon.com/images/caveman.gif"
              alt="HackerNoon Newsletter"
              loading="lazy"
              decoding="async"
              width="400"
              height="225"
            />
          </TopCol>

          <TopCol>
            <NewsletterTitle>The HackerNoon Newsletter</NewsletterTitle>
            <NewsletterBlurb>Join 4M+ hackers getting the best tech stories in their inbox every week.</NewsletterBlurb>
            <SubscriptionWrap>
              <SubscriptionForm colors={colors} staticData={staticData} />
            </SubscriptionWrap>

            <AppGrid>
              {APP_LINKS.map(app => (
                <AppButton
                  key={app.label}
                  href={app.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  $icon={app.iconSize}
                  onClick={() => logFooterClick(app.label)}
                >
                  <i className={`hn ${app.icon}`} aria-hidden="true" />
                  <span className="labels">
                    <span className="kicker">{app.kicker}</span>
                    <span className="store">{app.label}</span>
                  </span>
                </AppButton>
              ))}
            </AppGrid>
          </TopCol>
        </Top>

        {/* Links: 5-col grid (desktop) / accordions (mobile) */}
        <Nav aria-label="Footer">
          <LinksGrid>
            {sections.map((section, i) => {
              const listId = `footer-section-${i}`;
              const isOpen = openSection === i;

              return (
                <div key={section.order ?? i}>
                  {/* Mobile accordion header */}
                  <AccordionButton
                    type="button"
                    $open={isOpen}
                    onClick={() => setOpenSection(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    aria-controls={listId}
                  >
                    {section.header}
                    <span className="plus" aria-hidden="true">
                      +
                    </span>
                  </AccordionButton>

                  {/* Desktop header */}
                  <DesktopHeading>
                    <FooterLink href={section.headerUrl} isHeader>
                      {section.header}
                    </FooterLink>
                  </DesktopHeading>

                  <LinkList id={listId} $open={isOpen}>
                    {/* Keeps the header link reachable on mobile */}
                    <MobileOnlyItem>
                      <FooterLink href={section.headerUrl} isHeader>
                        View all {titleCase(section.header?.toLowerCase())}
                      </FooterLink>
                    </MobileOnlyItem>
                    {(section.sections || []).map((item, idx) => (
                      <LinkItem key={idx}>
                        <FooterLink href={item.url}>{titleCase(item.text)}</FooterLink>
                      </LinkItem>
                    ))}
                  </LinkList>
                </div>
              );
            })}
          </LinksGrid>
        </Nav>

        {/* Socials: one row (desktop) / 7-col grid (mobile) */}
        <Socials>
          {Object.entries(HN_SOCIALS).map(([platform, url]) => (
            <a key={platform} href={url} target="_blank" rel="noopener noreferrer nofollow" aria-label={platform}>
              <i className={`hn hn-${platform}`} aria-hidden="true" />
            </a>
          ))}
        </Socials>

        {/* Brand + tagline */}
        <Brand>
          <a href="https://hackernoon.com" aria-label="HackerNoon Homepage">
            <HackerNoonLogo color={C.accent} width={200} height={25} />
          </a>
          <Divider aria-hidden="true" />
          <Tagline>How hackers start their afternoons.</Tagline>
        </Brand>

        {/* Copyright */}
        <Copyright>
          © {currentYear} HackerNoon. All rights reserved
          <DesktopOnly>&nbsp;·&nbsp;</DesktopOnly>
          <MobileBreak />
          56 Edwards Village Blvd, Unit 124-3000-243, <MobileBreak />
          Edwards, Colorado 81632, USA
        </Copyright>
      </FooterInner>
    </FooterLayout>
  );
};

export default Footer;
